/**
 * MasterDataContractTypeFormDialog — Create/Edit `contract_type`.
 * Compact Sales Contract style.
 *
 * `contract_type` là PK text: required, disable khi edit. VBA: trống →
 * "Please type Contract Type!"; trùng (create) → "Contract type is existed!"
 * (BE raise → snackbar).
 */
import {
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { useEffect, useState } from 'react'

import { useSnackbar } from '@/hooks/useSnackbar'
import {
  useCreateMdContractTypeMutation,
  useUpdateMdContractTypeMutation,
} from '@/store/api/masterDataCrudApi'
import type { MasterDataContractType } from '@/types/api'
import { getErrorMessage } from '@/utils/error'

import { compactDialogContentSx, dialogHeaderBtnSx, dialogTitleActionsSx, dialogTitleSx, dialogTitleTextSx, roSx } from '../purchase/dialogStyle'

export interface Props {
  open: boolean
  mode: 'create' | 'edit'
  initialValue?: MasterDataContractType | null
  onClose: () => void
  onSaved?: () => void
}

type FormState = {
  contract_type: string
}

const EMPTY: FormState = {
  contract_type: '',
}

export default function MasterDataContractTypeFormDialog({
  open,
  mode,
  initialValue,
  onClose,
  onSaved,
}: Props) {
  const snack = useSnackbar()
  const isEdit = mode === 'edit'

  const [createContractType, { isLoading: creating }] = useCreateMdContractTypeMutation()
  const [updateContractType, { isLoading: updating }] = useUpdateMdContractTypeMutation()
  const loading = creating || updating

  const [form, setForm] = useState<FormState>(EMPTY)
  const [showErrors, setShowErrors] = useState(false)

  useEffect(() => {
    if (!open) return
    setShowErrors(false)
    if (isEdit && initialValue) {
      setForm({
        contract_type: initialValue.contract_type ?? '',
      })
    } else {
      setForm(EMPTY)
    }
  }, [open, isEdit, initialValue])

  const set = <K extends keyof FormState>(k: K, v: FormState[K]) =>
    setForm((p) => ({ ...p, [k]: v }))

  const codeEmpty = !form.contract_type.trim()
  const canSave = !codeEmpty && !loading

  const handleClose = () => {
    if (loading) return
    onClose()
  }

  const handleSave = async () => {
    if (codeEmpty) {
      setShowErrors(true)
      return
    }
    try {
      if (isEdit && initialValue) {
        await updateContractType({ code: initialValue.contract_type, body: {} }).unwrap()
        snack.success('Contract type updated')
      } else {
        await createContractType({ contract_type: form.contract_type.trim() }).unwrap()
        snack.success('Contract type created')
      }
      onSaved?.()
      onClose()
    } catch (e) {
      snack.error(getErrorMessage(e, isEdit ? 'Failed to update' : 'Failed to create'))
    }
  }

  return (
    <Dialog
      open={open}
      onClose={(_event, reason) => {
        if (loading) return
        if (reason === 'backdropClick') return
        handleClose()
      }}
      maxWidth="sm"
      fullWidth
      scroll="paper"
    >
      <DialogTitle sx={dialogTitleSx}>
        <Typography component="span" noWrap sx={dialogTitleTextSx}>
          {isEdit ? 'Edit Contract Type' : 'New Contract Type'}
          {isEdit && initialValue ? (
            <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
              {initialValue.contract_type}
            </Typography>
          ) : null}
        </Typography>
        <Stack direction="row" spacing={0.5} sx={dialogTitleActionsSx}>
          <Button
            type="button"
            variant="contained"
            size="small"
            color="success"
            onClick={() => void handleSave()}
            disabled={!canSave}
            sx={dialogHeaderBtnSx}
          >
            {loading ? <CircularProgress size={14} color="inherit" /> : 'Save'}
          </Button>
          <Button type="button" variant="outlined" size="small" onClick={handleClose} disabled={loading} sx={dialogHeaderBtnSx}>
            Cancel
          </Button>
        </Stack>
      </DialogTitle>
      <Divider />
      <DialogContent dividers sx={compactDialogContentSx}>
        <Grid container spacing={1.5} sx={{ mt: 0 }}>
          <Grid item xs={12}>
            <TextField
              label="Contract Type"
              size="small"
              fullWidth
              required
              disabled={isEdit}
              error={showErrors && codeEmpty}
              value={form.contract_type}
              onChange={(e) => set('contract_type', e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ tabIndex: isEdit ? -1 : 1 }}
              helperText={isEdit ? 'Contract Type cannot be changed after creation.' : undefined}
              sx={isEdit ? roSx : undefined}
            />
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  )
}
